import Draggable16 from '@carbon/icons-react/es/draggable/16';
import Export16 from '@carbon/icons-react/es/export/16';
import { Space, Tag, Tooltip } from 'antd';
import React, {
  FC,
  ReactElement,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SimpleSearchFilter, WidgetType } from 'api/dashboard/widget';
import { Project } from 'api/project';
import { Button } from 'components/atoms/button';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { Modal } from 'components/atoms/modal';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { TableSkeleton } from 'components/atoms/table';
import { Filter } from 'components/molecules/filters/types';
import { Widget } from 'components/organisms/csat/overview/widgets/widget';
import { DashBoardTasksList } from 'components/organisms/tasks/dashboard-task-list';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { DashBoardProjectList } from 'routes/projects/list-v2/dashboards-project-list';
import { FilterAsText } from '../filter-as-text';
import { SortTypes } from './chart-formatting-utils';
import {
  TitleWrapper,
  FlexRow,
  ListWrapper,
  ModalWrapper,
  DrilldownTitleWrapper,
  StyledSpace,
  ChartContent,
  Wrapper,
  ExpandIcon,
  InfoIcon,
  Title,
  SortAscendingIcon,
  SortDescendingIcon,
  NoSortIcon,
  ModalIconsWrapper,
  ProductiveHeading6,
  BodyShort2,
  InfoIconLarge,
} from './styles';
import { ObjectType } from './types';
import { useDashboardExports } from './utils';
import { useChartDndContext } from './widget-dnd';

const HighChartWrapper = styled.div`
  height: calc(100% - var(--spacing-09));

  div[data-highcharts-chart] {
    height: 100%;
  }

  .highcharts-root {
    height: 100%;
  }
`;
const Dnd = styled.span`
  display: none;
  height: 16px;
  width: 16px;
  &:hover {
    cursor: grab;
  }
`;

const StyledWidget = styled(Widget)`
  height: 100%;

  .highcharts-scrollbar {
    height: 0;
    opacity: 0;
    transition: opacity 0.5s;
  }

  &:hover ${Wrapper} {
    opacity: 1;
  }

  &:hover ${Dnd} {
    display: block;
  }

  &:hover .highcharts-scrollbar {
    opacity: 1;
  }
`;

interface ChartWidgetGridProps {
  widgetId?: number;
  filter: GroupedFilter;
  dashboardFilter?: Filter;
  sortType: number;
  setSortType: React.Dispatch<React.SetStateAction<number>>;
  widgetName: string;
  chartContent: JSX.Element | null;
  preview?: boolean;
  chartMenu?: ReactElement;
  expandedChartContent?: JSX.Element | null;
  selectedSeries?: Highcharts.SeriesOptionsType | string | null;
  loading?: boolean;
  listData?: Project[];
  setData?: React.Dispatch<React.SetStateAction<Project[]>>;
  setSelectedSeries?: React.Dispatch<
    React.SetStateAction<Highcharts.SeriesOptionsType | string | null>
  >;
  closeModal?: () => void;
  isModalVisible?: boolean;
  isDateField: boolean;
  widgetType: WidgetType;
  objectType?: string;
  fieldNames?: string[];
  widgetSimpleFilter?: SimpleSearchFilter;
  dashboardSimpleFilter?: SimpleSearchFilter;
}

const ModalHeader = ({
  widgetId,
  widgetName,
  selectedSeries,
  widgetSimpleFilter,
  dashboardSimpleFilter,
}) => {
  const translate = useTranslate();
  const { exportFn, isExportInProgress } = useDashboardExports(
    widgetId as number,
  );
  const exportData = useCallback(() => {
    exportFn({
      drillDownFilter: widgetSimpleFilter,
      dashboardFilter: dashboardSimpleFilter,
    });
  }, [exportFn, dashboardSimpleFilter, widgetSimpleFilter]);

  return (
    <StyledSpace direction="vertical" size={4}>
      {selectedSeries ? (
        <Tag
          color={
            typeof selectedSeries === 'string'
              ? ''
              : (selectedSeries.color as string)
          }
        >
          {typeof selectedSeries === 'string'
            ? selectedSeries
            : selectedSeries?.name}
        </Tag>
      ) : null}
      <DrilldownTitleWrapper>
        <div>{widgetName}</div>
        <Button
          icon={<Export16 />}
          iconPlacement="right"
          loading={isExportInProgress}
          onClick={exportData}
          type="ghost"
        >
          {translate('export')}
        </Button>
      </DrilldownTitleWrapper>
    </StyledSpace>
  );
};

const ExpandModal = ({
  expandedChartContent,
  isModalVisible,
  widgetName,
  setModalVisiblity,
  getIcon,
  sortType,
  filter,
  isDateField,
  widgetType,
}) => {
  const [show, setShow] = useState(false);
  const sortText = useTranslation('dashbaord.custom.chart.sort');
  const yAxis = useTranslation('dashbaord.custom.chart.sort.yAxis');

  useEffect(() => {
    if (!isModalVisible) {
      setShow(false);

      return;
    }
    setTimeout(() => setShow(true), 500);
  }, [isModalVisible]);

  return (
    <Modal
      centered
      visible={isModalVisible}
      title={
        <Space direction="horizontal" size={4}>
          {filter.groupCriteria.length === 0 ? null : (
            <Tooltip
              overlayStyle={{ maxWidth: 'unset' }}
              title={<FilterAsText filter={filter} />}
            >
              <InfoIconLarge />
            </Tooltip>
          )}
          <div>{widgetName}</div>
        </Space>
      }
      footer={null}
      width="85%"
      onCancel={() => setModalVisiblity(false)}
    >
      <ModalWrapper>
        {!isDateField && WidgetType.CustomChart === widgetType && (
          <ModalIconsWrapper>
            <ProductiveHeading6>{`${sortText}: `}</ProductiveHeading6>
            <BodyShort2>{yAxis}</BodyShort2>
            {getIcon(sortType)}
          </ModalIconsWrapper>
        )}
        {show && expandedChartContent ? (
          <HighChartWrapper>{expandedChartContent}</HighChartWrapper>
        ) : null}
      </ModalWrapper>
    </Modal>
  );
};

const TasksList = memo(DashBoardTasksList);

export const ChartWidgetContent: FC<ChartWidgetGridProps> = (
  props: ChartWidgetGridProps,
) => {
  const {
    filter,
    dashboardFilter,
    widgetName,
    sortType,
    setSortType,
    chartMenu,
    chartContent,
    preview = false,
    isDateField,
    objectType,
    ...rest
  } = props;
  const [isModalVisible, setModalVisiblity] = useState<boolean>(false);
  const { listeners, attributes } = useChartDndContext();

  const setNextSort = useCallback(
    (isDate) => {
      // date should have only two sort - ascending,descending
      if (isDate) {
        switch (sortType) {
          case SortTypes.SortAscending:
          case SortTypes.SortDefault:
            setSortType(SortTypes.SortDescending);
            break;
          case SortTypes.SortDescending:
            setSortType(SortTypes.SortAscending);
            break;
          default:
            break;
        }

        return;
      }

      switch (sortType) {
        case SortTypes.SortAscending:
          setSortType(SortTypes.SortDescending);
          break;
        case SortTypes.SortDescending:
          setSortType(SortTypes.SortDefault);
          break;
        case SortTypes.SortDefault:
          setSortType(SortTypes.SortAscending);
          break;
        default:
          break;
      }
    },
    [setSortType, sortType],
  );

  const getIcon = useCallback(
    (type, isDate) => {
      switch (type) {
        case SortTypes.SortDefault:
          return isDate ? (
            <SortAscendingIcon onClick={() => setNextSort(isDate)} />
          ) : (
            <NoSortIcon onClick={() => setNextSort(isDate)} />
          );
        case SortTypes.SortDescending:
          return <SortAscendingIcon onClick={() => setNextSort(isDate)} />;
        case SortTypes.SortAscending:
          return <SortDescendingIcon onClick={() => setNextSort(isDate)} />;
        default:
          return null;
      }
    },
    [setNextSort],
  );

  const getTable = () => {
    if (rest.loading) {
      return <TableSkeleton rowCount={9} />;
    }

    if (
      objectType === ObjectType.Project ||
      ![WidgetType.TaskByStatus, WidgetType.CustomChart].includes(
        rest.widgetType,
      )
    ) {
      return (
        <DashBoardProjectList
          data={rest.listData || []}
          fieldNames={rest.fieldNames || []}
        />
      );
    }

    if (
      ObjectType.Task === objectType &&
      rest.dashboardSimpleFilter &&
      rest.widgetSimpleFilter &&
      rest.widgetId
    ) {
      return (
        <TasksList
          widgetId={rest.widgetId}
          widgetGroupedFilter={filter}
          dashboardFilter={rest.dashboardSimpleFilter}
          widgetFilter={rest.widgetSimpleFilter}
          fieldNames={rest.fieldNames || []}
        />
      );
    }

    return null;
  };

  const onCancel = useCallback(() => {
    rest.closeModal?.();
    rest.setSelectedSeries?.(null);
    rest.setData?.([]);
  }, [rest]);

  return (
    <StyledWidget
      title={
        <FlexRow>
          <TitleWrapper>
            {listeners && attributes ? (
              <Dnd {...listeners} {...attributes}>
                <Draggable16 />
              </Dnd>
            ) : null}

            <Title>{widgetName}</Title>
            {filter.groupCriteria.length ||
            dashboardFilter?.criteria?.length ? (
              <Tooltip
                overlayStyle={{ maxWidth: 'unset' }}
                title={
                  <FilterAsText
                    filter={filter}
                    dashboardFilter={dashboardFilter}
                  />
                }
              >
                <InfoIcon />
              </Tooltip>
            ) : null}
          </TitleWrapper>

          <Wrapper>
            {rest.widgetType === WidgetType.CustomChart && (
              <Tooltip
                title={<FormattedMessage id="dashbaord.custom.chart.sort" />}
              >
                <Button
                  type="text"
                  size="small"
                  icon={
                    <IconWrapper>{getIcon(sortType, isDateField)}</IconWrapper>
                  }
                />
              </Tooltip>
            )}
            {!preview && (
              <Tooltip title={<FormattedMessage id="expand" />}>
                <Button
                  onClick={() => setModalVisiblity(true)}
                  type="text"
                  size="small"
                  icon={
                    <IconWrapper>
                      <ExpandIcon />
                    </IconWrapper>
                  }
                />
              </Tooltip>
            )}
            {chartMenu && <MoreActionsDropdown menu={chartMenu} />}
          </Wrapper>
        </FlexRow>
      }
    >
      <ChartContent>{chartContent}</ChartContent>
      {!preview && !isModalVisible && (
        <Modal
          visible={rest.isModalVisible}
          confirmLoading={false}
          onCancel={onCancel}
          footer={null}
          destroyOnClose
          title={
            <ModalHeader
              widgetId={rest.widgetId}
              widgetName={widgetName}
              selectedSeries={rest.selectedSeries}
              widgetSimpleFilter={rest.widgetSimpleFilter}
              dashboardSimpleFilter={rest.dashboardSimpleFilter}
            />
          }
          maskClosable={false}
          keyboard={false}
          width="80%"
        >
          <ListWrapper>{getTable()}</ListWrapper>
        </Modal>
      )}
      <ExpandModal
        expandedChartContent={rest.expandedChartContent}
        isModalVisible={isModalVisible}
        setModalVisiblity={setModalVisiblity}
        widgetName={widgetName}
        getIcon={getIcon}
        sortType={sortType}
        filter={filter}
        isDateField={isDateField}
        widgetType={rest.widgetType}
      />
    </StyledWidget>
  );
};
